import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useFocusTrap } from '../../hooks/useFocusTrap';

export function SaveDrawingDialog({
  initialTitle,
  heading,
  saveLabel,
  description,
  onDiscard,
  blocked = false,
  maxLength = 200,
  error,
  onClose,
  onSave
}: {
  readonly initialTitle: string;
  readonly heading?: string;
  readonly saveLabel?: string;
  readonly description?: string;
  readonly onDiscard?: () => void;
  readonly blocked?: boolean;
  readonly maxLength?: number;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSave: (title: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(true, dialogRef);
  useEscapeKey(!busy && !blocked, onClose);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    inputRef.current?.focus();
    inputRef.current?.select();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  return (
    <div className="export-dialog-backdrop">
      <div
        ref={dialogRef}
        className="save-drawing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-drawing-title"
      >
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            if (busy || blocked || !title.trim()) return;
            setBusy(true);
            setFailed(false);
            try {
              if (await onSave(title.trim())) onClose();
              else setFailed(true);
            } finally {
              setBusy(false);
            }
          }}
        >
          <h2 id="save-drawing-title">{heading ?? t('workspace.actionBar.save')}</h2>
          {description ? <p>{description}</p> : null}
          <label className="drawing-title-editor">
            <span>{t('persistencePanel.titleLabel')}</span>
            <input
              ref={inputRef}
              value={title}
              maxLength={maxLength}
              required
              disabled={busy || blocked}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          {failed ? <p role="alert">{error ?? t('persistencePanel.messages.saveFailed')}</p> : null}
          <div className="persistence-actions">
            <button type="button" onClick={onClose} disabled={busy || blocked}>
              {t('exportDialog.cancel')}
            </button>
            {onDiscard ? (
              <button type="button" onClick={onDiscard} disabled={busy || blocked}>
                {t('persistencePanel.discardAndContinue')}
              </button>
            ) : null}
            <button type="submit" disabled={busy || blocked || !title.trim()}>
              {busy ? t('persistencePanel.saving') : (saveLabel ?? t('persistencePanel.createAndSave'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
